import { Box, Button, Stack, Typography } from "@mui/material";
import { ErrorBoundary, ErrorBoundaryProps } from "@sentry/react";
import { ErrorCoverSvg } from "@sinch/components/assets";
import { findGraphqlErrorContext } from "@sinch/core/tools/apolloMiddleware/graphqlErrorContext";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";

/**
 * Sentry error boundary component
 */
export const SentryErrorBoundary: FC<ErrorBoundaryProps> = ({ children, ...props }) => {
  const { t } = useTranslation();

  return (
    <ErrorBoundary
      // A failed GraphQL operation is reported twice: by the apollo error link (which knows the operation) and
      // again here, when the consuming component rethrows it. Attach the context of the failure so this event
      // names the operation as well instead of being an anonymous ApolloError.
      beforeCapture={(scope, error) => {
        const graphqlContext = findGraphqlErrorContext(error);
        if (graphqlContext) {
          scope.setTag("http.status_code", graphqlContext.httpCode);
          scope.setContext("Graphql", graphqlContext);
        }
      }}
      fallback={(params) => (
        <Stack
          spacing={2}
          sx={{
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: "60%",
            }}
          >
            <ErrorCoverSvg />
          </Box>
          <Typography variant="h1">{t("Error.somethingWentWrong")}</Typography>
          <Typography variant="h4">{t("Error.anErrorHasOccurred")}</Typography>
          <Typography>{t("Error.weApologiseForTheInconvenience")}</Typography>
          <Typography
            sx={{
              fontSize: "10px",
            }}
          >
            {t("Error.errorId")}: {params.eventId}
          </Typography>
          <Button fullWidth={false} onClick={() => globalThis.location.reload()} size="large" variant="contained">
            {t("Action.reloadPage")}
          </Button>
        </Stack>
      )}
      {...props}
    >
      {children}
    </ErrorBoundary>
  );
};
